<template>
  <div class="enabled-product-tile" :class="{ 'h-100': parentIsDashboard }" @click.stop>
    <!-- AO's root rather than <body>, which embedded is outside AO's CSS scope. -->
    <Teleport :to="scopeSelector" :disabled="parentIsDashboard">
      <vue-draggable-resizable
        :w="!parentIsDashboard ? settings.width : 'auto'"
        :h="!parentIsDashboard ? settings.height : 'auto'"
        :parent="false"
        :z="Math.max(settings.zIndex || 200, 200)"
        :style="`${settings.left ? `left: ${settings.left}px;` : ''}${
          settings.right ? `right: ${settings.right}px;` : ''
        } top: ${settings.top}px; max-height: ${
          !parentIsDashboard ? settings.height + 'px' : 'auto'
        };`"
        :drag-cancel="'.drag-cancel'"
        :class="{
          'drag-cancel': parentIsDashboard,
          'draggable-resizable': !parentIsDashboard,
          'draggable-fit-content': !parentIsDashboard,
        }"
      >
        <div :class="{ 'd-flex flex-column product-content-container': !parentIsDashboard }">
          <div v-if="!parentIsDashboard" class="d-flex justify-space-between heading mb-1">
            <div class="product-title">
              {{ productInfo.itemDescription }}
            </div>
            <div class="close" @click.stop="$emit('close-popup', productIndex)">
              <v-icon :size="18" color="primary"> mdi-close-circle </v-icon>
            </div>
          </div>
          <div>
            <p class="mb-1 copy-text">
              <img class="mr-1" :src="copyIcon" />
              {{ $t('assortmentCanvas.copyText') }}
            </p>
          </div>
          <div
            :class="{
              'd-flex': true,
              'main-body': true,
              'flex-column': !parentIsDashboard,
            }"
          >
            <div v-if="!parentIsDashboard" class="popup-left-side">
              <div class="d-flex product-tile position-relative flex-column justify-start">
                <div class="image-wrapper">
                  <AssortmentImage
                    v-bind="conditionalProductTileStyle"
                    :src="imageUrl"
                    :alt="imageAlt"
                    :title="imageTitle"
                    :tooltip-text="`${imageTitle} - ${productInfo.itemDescription}`"
                    :tag-text="imageTagText"
                    :show-not-in-reset-icon="!productInfo.inReset"
                    dashboard
                  />
                </div>
              </div>
              <div class="d-flex flex-column info-table justify-center flex-grow-3">
                <div class="d-flex justify-space-between product-tooltip-row">
                  <div
                    class="font-weight-bold mr-4 copy-to-clipboard"
                    @click="copyToClipboard(`${productInfo.productKeyDisplay}`, $event)"
                  >
                    {{ $t('assortmentCanvas.productKeyDisplay') }}
                  </div>
                  <div :title="productInfo.productKeyDisplay" class="overflow-ellipsis">
                    {{ productInfo.productKeyDisplay }}
                  </div>
                </div>
                <div
                  v-if="productInfo.futureProductIdString"
                  class="d-flex justify-space-between product-tooltip-row"
                >
                  <div
                    class="font-weight-bold mr-4 copy-to-clipboard"
                    @click="copyToClipboard(`${productInfo.futureProductIdString}`, $event)"
                  >
                    {{ $t('assortmentCanvas.futureProductId') }}
                  </div>
                  <div :title="productInfo.futureProductIdString" class="overflow-ellipsis">
                    {{ productInfo.futureProductIdString }}
                  </div>
                </div>
                <div class="d-flex justify-space-between product-tooltip-row">
                  <div
                    class="font-weight-bold mr-4 copy-to-clipboard"
                    @click="copyToClipboard(`${productInfo.itemDescription}`, $event)"
                  >
                    {{ $t('assortmentCanvas.productDescription') }}
                  </div>
                  <div :title="productInfo.itemDescription" class="overflow-ellipsis">
                    {{ productInfo.itemDescription }}
                  </div>
                </div>
                <div class="d-flex justify-space-between product-tooltip-row">
                  <div
                    class="font-weight-bold mr-4 copy-to-clipboard"
                    @click="
                      copyToClipboard(
                        `${formatNumber({
                          number: productInfo.contentValue,
                          format: 'float',
                        })}`,
                        $event
                      )
                    "
                  >
                    {{ $t('assortmentCanvas.contentUom') }}
                  </div>
                  <div
                    :title="`${formatNumber({
                      number: productInfo.contentValue,
                      format: 'float',
                    })} ${productInfo.contentUnitOfMeasure}`"
                    class="overflow-ellipsis"
                  >
                    {{ formatNumber({ number: productInfo.contentValue, format: 'float' }) }}
                    {{ productInfo.contentUnitOfMeasure }}
                  </div>
                </div>
                <div class="d-flex justify-space-between product-tooltip-row">
                  <div
                    class="font-weight-bold mr-4 copy-to-clipboard"
                    @click="
                      copyToClipboard(
                        `${formatNumber({ number: productInfo.size, format: 'float' })}`,
                        $event
                      )
                    "
                  >
                    {{ $t('assortmentCanvas.dimensions') }}
                  </div>
                  <div
                    :title="formatNumber({ number: productInfo.size, format: 'float' })"
                    class="overflow-ellipsis"
                  >
                    {{ formatNumber({ number: productInfo.size, format: 'float' }) }}
                  </div>
                </div>
              </div>
            </div>
            <div
              class="d-flex flex-column"
              :class="{
                'popup-right-side': !parentIsDashboard,
                'popup-centre': parentIsDashboard,
              }"
            >
              <div class="d-flex flex-column">
                <div v-if="parentIsDashboard" class="d-flex product-dashboard position-relative">
                  <div>
                    <AssortmentImage
                      v-bind="conditionalProductTileStyle"
                      :src="imageUrl"
                      :alt="imageAlt"
                      :title="imageTitle"
                      :tag-text="imageTagText"
                      :tooltip-text="`${imageTitle} - ${productInfo.itemDescription}`"
                      :show-not-in-reset-icon="!productInfo.inReset"
                      dashboard
                    />
                  </div>
                </div>
              </div>
              <div class="d-flex flex-column info-table">
                <!-- Selected Kpis -->
                <div
                  v-for="(kpi, i) in selectedKpis"
                  :key="i"
                  class="d-flex justify-space-between product-tooltip-row kpi-row"
                >
                  <div
                    class="font-weight-bold mr-4 copy-to-clipboard"
                    @click="copyToClipboard(getKPIValue(kpi), $event)"
                  >
                    {{ getKpiTitle(kpi) }}
                  </div>
                  <div class="overflow-ellipsis" :title="`${getKPIValue(kpi)}`">
                    <span>{{ getKPIValue(kpi) }}</span>
                  </div>
                </div>
                <div
                  v-if="productInfo.averageTransactionWeight"
                  class="d-flex justify-space-between product-tooltip-row"
                >
                  <div
                    class="font-weight-bold mr-4 copy-to-clipboard"
                    @click="
                      copyToClipboard(
                        `${formatNumber({
                          number: productInfo.averageTransactionWeight * productInfo.contentValue,
                          format: 'float',
                        })}`,
                        $event
                      )
                    "
                  >
                    {{ $t('assortmentCanvas.volumeUom') }}
                  </div>
                  <div
                    :title="
                      formatNumber({
                        number: productInfo.averageTransactionWeight * productInfo.contentValue,
                        format: 'float',
                      })
                    "
                    class="overflow-ellipsis"
                  >
                    {{
                      `${formatNumber({
                        number: productInfo.averageTransactionWeight * productInfo.contentValue,
                        format: 'float',
                      })} ${productInfo.contentUnitOfMeasure}`
                    }}
                  </div>
                </div>
                <div class="d-flex justify-space-between product-tooltip-row divider-top">
                  <div
                    class="font-weight-bold mr-4 copy-to-clipboard"
                    @click="
                      copyToClipboard(
                        `${$t('assortmentCanvas.productPositioning', [
                          podAndStoreCountInfo.originalSpacebreak.shortName,
                          podAndStoreCountInfo.originalSpacebreak.storeCount,
                          podAndStoreCountInfo.originalSpacebreak.pod,
                        ])}`,
                        $event
                      )
                    "
                  >
                    {{ $t('assortmentCanvas.startingPointPosition') }}
                  </div>
                  <div
                    :title="
                      $t('assortmentCanvas.productPositioning', [
                        podAndStoreCountInfo.originalSpacebreak.shortName,
                        podAndStoreCountInfo.originalSpacebreak.storeCount,
                        podAndStoreCountInfo.originalSpacebreak.pod,
                      ])
                    "
                    class="overflow-ellipsis"
                  >
                    {{
                      $t('assortmentCanvas.productPositioning', [
                        podAndStoreCountInfo.originalSpacebreak.shortName,
                        podAndStoreCountInfo.originalSpacebreak.storeCount,
                        podAndStoreCountInfo.originalSpacebreak.pod,
                      ])
                    }}
                  </div>
                </div>
                <div class="d-flex justify-space-between product-tooltip-row">
                  <div
                    class="font-weight-bold mr-4 copy-to-clipboard"
                    @click="
                      copyToClipboard(
                        `${$t('assortmentCanvas.productPositioning', [
                          podAndStoreCountInfo.optimisedSpacebreak.shortName,
                          podAndStoreCountInfo.optimisedSpacebreak.storeCount,
                          podAndStoreCountInfo.optimisedSpacebreak.pod,
                        ])}`,
                        $event
                      )
                    "
                  >
                    {{ $t('assortmentCanvas.lastOptimisation') }}
                  </div>
                  <div
                    :title="
                      $t('assortmentCanvas.productPositioning', [
                        podAndStoreCountInfo.optimisedSpacebreak.shortName,
                        podAndStoreCountInfo.optimisedSpacebreak.storeCount,
                        podAndStoreCountInfo.optimisedSpacebreak.pod,
                      ])
                    "
                    class="overflow-ellipsis"
                  >
                    {{
                      $t('assortmentCanvas.productPositioning', [
                        podAndStoreCountInfo.optimisedSpacebreak.shortName,
                        podAndStoreCountInfo.optimisedSpacebreak.storeCount,
                        podAndStoreCountInfo.optimisedSpacebreak.pod,
                      ])
                    }}
                  </div>
                </div>
                <div class="d-flex justify-space-between product-tooltip-row">
                  <div
                    class="font-weight-bold mr-4 copy-to-clipboard"
                    @click="
                      copyToClipboard(
                        `${$t('assortmentCanvas.productPositioning', [
                          podAndStoreCountInfo.currentSpacebreak.shortName,
                          podAndStoreCountInfo.currentSpacebreak.storeCount,
                          podAndStoreCountInfo.currentSpacebreak.pod,
                        ])}`,
                        $event
                      )
                    "
                  >
                    {{ $t('assortmentCanvas.latestEdit') }}
                  </div>
                  <div
                    :title="
                      $t('assortmentCanvas.productPositioning', [
                        podAndStoreCountInfo.currentSpacebreak.shortName,
                        podAndStoreCountInfo.currentSpacebreak.storeCount,
                        podAndStoreCountInfo.currentSpacebreak.pod,
                      ])
                    "
                    class="overflow-ellipsis"
                  >
                    {{
                      $t('assortmentCanvas.productPositioning', [
                        podAndStoreCountInfo.currentSpacebreak.shortName,
                        podAndStoreCountInfo.currentSpacebreak.storeCount,
                        podAndStoreCountInfo.currentSpacebreak.pod,
                      ])
                    }}
                  </div>
                </div>
              </div>
              <div class="d-flex flex-column">
                <div class="d-flex justify-space-between sub-info-row align-center">
                  <div class="font-weight-bold mr-2">
                    {{ $t('assortmentCanvas.spacebreak') }}
                  </div>
                  <div class="select-container select-container--spacebreak mt-4">
                    <!-- A copy of the select with the notEligible or notForAssortment option
                  The reason for that is that the products in the not listed and not eligible spacebreaks
                  have currentSpacebreakId === null. Plus, we don't want products to move to the not Eligible spacebreak
                  by changing the selection.
                  -->
                    <v-select
                      v-if="
                        !productInfo.isEligible || !productInfo.assortment || productInfo.isLocal
                      "
                      :model-value="selectedSpacebreak"
                      :disabled="true"
                      :items="[
                        {
                          value: null,
                          title: $t(
                            `assortmentCanvas.${
                              !productInfo.isEligible
                                ? spacebreakTypes.notInStoreClass
                                : spacebreakTypes.notForAssortment
                            }`
                          ),
                          disabled: false,
                        },
                      ]"
                      class="spacebreak-select"
                    />

                    <v-select
                      v-if="
                        productInfo.isEligible && productInfo.assortment && !productInfo.isLocal
                      "
                      :model-value="selectedSpacebreak"
                      :items="spacebreaks"
                      :menu-props="{
                        top: true,
                        offsetY: true,
                        attach: parentIsDashboard ? '.select-container' : scopeSelector,
                      }"
                      :disabled="
                        productInfo.lockType === lockTypes.locked ||
                        spacebreakDisabled ||
                        !productInfo.inReset
                      "
                      @update:model-value="changeSpacebreak($event)"
                    />
                  </div>
                </div>
              </div>
              <div class="d-flex flex-column">
                <div class="d-flex justify-space-between sub-info-row">
                  <div
                    class="font-weight-bold mr-4 copy-to-clipboard"
                    @click="copyToClipboard(`${productInfo.optimisedNote}`, $event)"
                  >
                    {{ $t('assortmentCanvas.optimiserNotes') }}
                  </div>
                  <div :title="productInfo.optimisedNote" class="overflow-ellipsis">
                    {{ productInfo.optimisedNote }}
                  </div>
                </div>
              </div>
              <div v-if="productPallets.length > 0" class="d-flex flex-column mt-3">
                <div class="d-flex flex-column sub-info-row">
                  <div class="font-weight-bold mr-4">
                    {{ $t('general.pallets') }}
                  </div>
                  <div class="pallets">
                    <div v-for="pallet in productPallets" :key="pallet.productKeyDisplay">
                      <ProductPallet
                        :product="pallet"
                        :pallet-content-spacebreak="selectedSpacebreak"
                        :all-pallets="productPallets"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="d-flex px-3 pt-3 pb-2">
          <button v-if="!parentIsDashboard" class="button" @click="popupButton.action()">
            {{ popupButton.label }}
          </button>
        </div>
        <div id="copy-tooltip" class="copy-tooltip-hidden">
          {{ $t('assortmentCanvas.copyTooltip') }}
        </div>
      </vue-draggable-resizable>
    </Teleport>
  </div>
</template>

<script>
// assets
import CopyIcon from '@/assets/copyToClipboard.svg';

// enums
import productKpis from '@enums/product-kpis';

// composables
import { useFormatNumber } from '@/composables/use-format-number';

// webshell
import { STYLE_SCOPE_CLASS } from '@/webshell-embed/embedded';

export default {
  name: 'ProductDraggableTooltip',

  props: {
    productPallets: {
      type: Array,
      required: false,
      default: () => [],
    },
    productInfo: {
      type: Object,
      required: true,
    },
    podAndStoreCountInfo: {
      type: Object,
      required: true,
    },
    settings: {
      type: Object,
      required: false,
      default: () => ({
        clientX: 0,
        clientY: 0,
      }),
    },
    conditionalProductTileStyle: {
      type: Object,
      required: true,
    },
    productIndex: {
      // required for parent components that hold products in an array instead of a single variable
      type: Number,
      required: false,
      default: () => 0,
    },
    cssZindex: {
      type: Number,
      required: false,
      default: () => 2,
    },
    productPositioningInfo: {
      type: Object,
      required: true,
    },
    // props for the pictures
    imageUrl: {
      type: String,
      required: false,
      default: '',
    },
    imageAlt: {
      type: String,
      required: false,
      default: '',
    },
    imageTitle: {
      type: String,
      required: false,
      default: '',
    },
    imageTagText: {
      type: String,
      required: false,
      default: '',
    },
    spacebreaks: {
      type: Array,
      required: true,
    },
    selectedSpacebreak: {
      type: Object,
      required: true,
    },
    changeSpacebreak: {
      type: Function,
      required: true,
    },
    lockTypes: {
      type: Object,
      required: true,
    },
    popupButton: {
      type: Object,
      required: true,
    },
    parentIsDashboard: {
      type: Boolean,
      required: false,
      default: false,
    },
    selectedKpis: {
      type: Array,
      required: true,
    },
    spacebreakDisabled: {
      type: Boolean,
      required: true,
    },
    spacebreakTypes: {
      type: Object,
      required: true,
    },
    copyIcon: {
      type: String,
      required: false,
      default: CopyIcon,
    },
    currentNumericLocale: {
      type: String,
      required: true,
    },
  },

  emits: ['close-popup'],
  setup() {
    const { formatNumber } = useFormatNumber();
    return { formatNumber, scopeSelector: `.${STYLE_SCOPE_CLASS}` };
  },

  methods: {
    getKPIValue(kpi) {
      if (kpi === productKpis.kpis.SELFSWITCHING || kpi === productKpis.kpis.SHRINK) {
        return `${this.productInfo[kpi] ? (this.productInfo[kpi] * 100).toFixed(2) : 0}%`;
      }

      if (kpi === productKpis.kpis.MARGIN) {
        if (!this.productInfo[kpi]) return '0%';

        const marginValueToShow = (this.productInfo[kpi] * 100).toFixed(0);

        return `${marginValueToShow}%`;
      }

      if (kpi === productKpis.kpis.UTILITY || kpi === productKpis.kpis.RANK)
        return this.productInfo[kpi];

      return this.formatNumber({
        number: this.productInfo[kpi] || 0,
        format: 'float',
      });
    },

    getKpiTitle(kpi) {
      if (kpi === productKpis.kpis.SALES) {
        return this.$t(`assortmentCanvas.${kpi}`, {
          currency: this.$t(`currencies.${this.currentNumericLocale}`),
        });
      }

      return this.$t(`assortmentCanvas.${kpi}`);
    },
    copyToClipboard(str, event) {
      const contentsToCopy = str || '';

      // Show the little tooltip at the right position with good old vanilla JS
      const copyTooltip = document.getElementById('copy-tooltip');
      copyTooltip.classList.remove('copy-tooltip-hidden');
      copyTooltip.style.left = `${event.srcElement.offsetLeft}px`;
      copyTooltip.style.top = `${event.srcElement.offsetTop - 30}px`;

      navigator.clipboard.writeText(contentsToCopy);

      // Hide the tooltip
      setTimeout(function () {
        copyTooltip.classList.add('copy-tooltip-hidden');
      }, 1000);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.close {
  cursor: pointer;
}
.heading {
  border-bottom: 1px solid $assortment-divider-colour;
}
.product-tooltip-row {
  font-size: 1.2rem;
  padding: 1px 6px;
  &:nth-child(odd) {
    background: $assortment-table-blue-bg-colour;
  }

  &.divider {
    border-bottom: 1px solid $assortment-menu-border-colour;
  }
  &.divider-top {
    border-top: 1px solid $assortment-menu-border-colour;
  }
}
.sub-info-row {
  font-size: 1.2rem;
  background: $assortment-table-white-bg-colour;
}

.popup-right-side {
  width: 100%;
}
.popup-left-side {
  width: 100%;
  display: flex;
  justify-content: center;
}
.popup-centre {
  width: 100%;
  display: flex;
  justify-content: center;
}
.draggable-resizable {
  position: fixed;
  background: white;
  box-shadow: $assortment-pop-out-box-shadow;

  &.active {
    // position the currently dragged tooltip on top of all the others
    z-index: 201 !important;
  }
}

.draggable-fit-content {
  height: fit-content !important;
}

.heading {
  padding: 7px 12px;
}

.main-body {
  padding: 16px;
}

.product-title {
  font-weight: bold;
  font-size: 1.3rem;
}

.product-tile {
  background-position: center center;
  background-size: contain;
  color: #000000;
  display: flex;
  justify-content: center; /* align horizontal */
  align-items: center; /* align vertical */
  font-size: 14px;
}

.product-dashboard {
  background-position: center center;
  background-size: contain;
  color: #000000;
  display: flex;
  justify-content: center; /* align horizontal */
  align-items: center; /* align vertical */
  padding-bottom: 10px;
}

.enabled-product-tile {
  pointer-events: visible;
}

.button {
  cursor: pointer;
  text-decoration: none;
  width: 100%;
  color: white;
  border-radius: 3px;
  background-color: $assortment-primary-colour;
  font-size: 1.4rem;
  border: none;
  text-decoration: none;
}

.drag-cancel {
  cursor: default;
}

.spacebreak-select {
  padding-left: 5px;
  width: 100%;
  height: 30px;
  border-bottom: 1px solid $assortment-primary-colour;
  outline: none;
  background: $assortment-control-secondary-bg-colour;
}

.select-container {
  width: 100%;
  position: relative;

  &--spacebreak {
    width: 75%;
  }

  .appended-icon {
    position: absolute;
    right: 0;
    top: 50%;
    transform: translate(0, -50%);

    &::before {
      font-size: 24px;
    }
  }
}

.info-table {
  // max-height and overflow set here for when additional client kpis are selected
  max-height: 300px;
  width: 100%;
  overflow: auto;
}

:deep(.select-container .v-input--is-disabled) {
  padding: 0;

  .v-input {
    &__control {
      min-height: 27px;
    }

    &__slot {
      height: 27px !important;

      &:before {
        border: none;
      }
    }
  }

  .v-select__slot {
    height: 27px !important;
  }
}

:deep(.product-content-container) {
  max-height: 560px;
  overflow-y: auto;
}

:deep(.image-wrapper) {
  position: relative;
  padding: 3px 2px 2px 2px;
  margin: 5px;
  align-items: center;
  border: 1px solid #d9dadd;
  border-radius: 5px;
  transition: all 1s ease-out;

  .absolute-height {
    height: 100% !important;
  }
}

.copy-to-clipboard {
  cursor: pointer;
  text-decoration: underline;
  color: $reporting-section-active-border;
}

.copy-text {
  font-size: 1.2rem;
  margin-left: 20px;
  margin-bottom: -15px !important;
}

#copy-tooltip {
  background: $assortment-canvas-tooltip-background;
  color: $assortment-input-background;
  border-radius: 4px;
  font-size: 14px;
  line-height: 22px;
  display: inline-block;
  padding: 5px 16px;
  position: absolute;
  text-transform: none;
  width: auto;
  opacity: 0.9;
  pointer-events: none;
  z-index: 199;
}
.copy-tooltip-hidden {
  display: none !important;
}
</style>
